import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

const EMPTY_FORM = {
  school_name:        '',
  school_code:        '',
  school_location:    '',
  school_tagline:     '',
  admin_username:     '',
  admin_password:   '',
  contact_name:       '',
  contact_mobile:     '',
  contact_email:      '',
  subscription_plan:  'basic',
};

export default function AdminPanel({ onLogout }) {
  const [schools,     setSchools]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [saving,      setSaving]      = useState(false);
  const [saveError,   setSaveError]   = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [deleting,    setDeleting]    = useState(null);   // school_code being deleted
  const [confirmCode, setConfirmCode] = useState('');     // user types school code to confirm
  const [resetting,   setResetting]   = useState(null);   // school_code being reset
  const [resetPass,   setResetPass]   = useState('');     // new temp password

  // ── Sub-user (staff) reset state ─────────────────────────────────────────
  // usersPanelSchool holds the school_code whose staff list is currently expanded.
  // subUsers[code] caches the list per school. resettingUser holds the user_id
  // currently in "type a new temp password" mode, and userResetPass its input.
  const [usersPanelSchool, setUsersPanelSchool] = useState(null);
  const [subUsers,         setSubUsers]         = useState({});
  const [subUsersLoading,  setSubUsersLoading]  = useState(false);
  const [resettingUser,    setResettingUser]    = useState(null);
  const [userResetPass,    setUserResetPass]    = useState('');

  // ── New additions ─────────────────────────────────────────────────────────
  const [searchQuery,   setSearchQuery]   = useState('');
  const [toggling,      setToggling]      = useState(null);    // school_code being toggled
  const [editingCode,   setEditingCode]   = useState(null);    // school_code being edited
  const [editForm,      setEditForm]      = useState({});      // edit form state
  const [editSaving,    setEditSaving]    = useState(false);
  const [statsSchool,   setStatsSchool]   = useState(null);    // school_code expanded for stats
  const [statsData,     setStatsData]     = useState({});      // { [code]: { data } }
  const [statsLoading,  setStatsLoading]  = useState(null);    // school_code loading stats
  const [togglingWA,       setTogglingWA]       = useState(null);  // school_code toggling whatsapp
  const [togglingCert,     setTogglingCert]     = useState(null);  // school_code toggling certificates
  const [togglingBio,      setTogglingBio]      = useState(null);  // school_code toggling biometric (legacy)
  const [togglingEmpBio,   setTogglingEmpBio]   = useState(null);  // school_code toggling employee biometric
  const [togglingStuBio,   setTogglingStuBio]   = useState(null);  // school_code toggling student biometric
  const [togglingSMS,      setTogglingSMS]      = useState(null);  // school_code toggling SMS
  const [smsLog,           setSmsLog]           = useState(null);  // null=hidden, []=[rows]
  const [smsLogLoading,    setSmsLogLoading]    = useState(false);

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await adminAPI.listSchools();
      setSchools(data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSchools(); }, [fetchSchools]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // Auto-generate school_code from school_name when user hasn't typed one yet
    if (name === 'school_name' && !form.school_code) {
      const code = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').slice(0, 30);
      setForm((f) => ({ ...f, school_name: value, school_code: code }));
    }
  };

  const handleResetPassword = async (school) => {
    if (!resetPass || resetPass.length < 4) {
      alert('Enter a temporary password (min 4 characters).');
      return;
    }
    try {
      const res = await adminAPI.resetPassword(school.school_code, resetPass);
      setSaveSuccess(res.data.message);
      setResetting(null);
      setResetPass('');
    } catch (err) {
      alert(err.response?.data?.message || 'Reset failed');
    }
  };

  // ── Sub-user (staff) reset handlers ──────────────────────────────────────
  const loadSubUsers = async (school) => {
    const code = school.school_code;
    // Toggle: click again to collapse
    if (usersPanelSchool === code) {
      setUsersPanelSchool(null);
      setResettingUser(null);
      setUserResetPass('');
      return;
    }
    setUsersPanelSchool(code);
    setResettingUser(null);
    setUserResetPass('');
    setSubUsersLoading(true);
    try {
      const { data } = await adminAPI.listSchoolUsers(code);
      setSubUsers((prev) => ({ ...prev, [code]: data.data || [] }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load users for this school');
      setUsersPanelSchool(null);
    } finally {
      setSubUsersLoading(false);
    }
  };

  const handleResetSubUser = async (schoolCode, user) => {
    if (!userResetPass || userResetPass.length < 4) {
      alert('Enter a temporary password (min 4 characters).');
      return;
    }
    try {
      const { data } = await adminAPI.resetSchoolUserPassword(
        schoolCode, user.user_id, userResetPass,
      );
      setSaveSuccess(data.message);
      setResettingUser(null);
      setUserResetPass('');
      // Refresh cached list so the "must change" badge shows up.
      try {
        const refreshed = await adminAPI.listSchoolUsers(schoolCode);
        setSubUsers((prev) => ({ ...prev, [schoolCode]: refreshed.data.data || [] }));
      } catch { /* non-fatal */ }
    } catch (err) {
      alert(err.response?.data?.message || 'Reset failed');
    }
  };

  const handleDelete = async (school) => {    // Require typing the school_code to confirm — prevents accidental deletion
    if (confirmCode !== school.school_code) {
      alert(`Type the school code "${school.school_code}" exactly to confirm deletion.`);
      return;
    }
    try {
      await adminAPI.deleteSchool(school.school_code);
      setSaveSuccess(`School "${school.school_name}" and all its data permanently deleted.`);
      setDeleting(null);
      setConfirmCode('');
      fetchSchools();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const res = await adminAPI.createSchool(form);
      setSaveSuccess(res.data.message);
      setForm(EMPTY_FORM);
      setShowForm(false);
      fetchSchools();
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to create school');
    } finally {
      setSaving(false);
    }
  };

  // ── New handlers ────────────────────────────────────────────────────────────
  const handleToggleActive = async (school) => {
    setToggling(school.school_code);
    try {
      const res = await adminAPI.toggleActive(school.school_code);
      setSaveSuccess(res.data.message);
      setSchools((prev) => prev.map((s) =>
        s.school_code === school.school_code ? { ...s, is_active: res.data.is_active } : s
      ));
    } catch (err) {
      alert(err.response?.data?.message || 'Toggle failed');
    } finally {
      setToggling(null);
    }
  };

  const handleToggleWhatsApp = async (school) => {
    setTogglingWA(school.school_code);
    try {
      const res = await adminAPI.toggleWhatsApp(school.school_code);
      setSaveSuccess(res.data.message);
      await fetchSchools();
    } catch (err) {
      alert(err.response?.data?.message || 'Toggle WhatsApp failed');
    } finally {
      setTogglingWA(null);
    }
  };

  const makeFeatureToggle = (setter, apiCall, flag, label) => async (school) => {
    setter(school.school_code);
    try {
      const res = await apiCall(school.school_code);
      setSaveSuccess(res.data.message);
      await fetchSchools();
    } catch (err) {
      alert(err.response?.data?.message || `Toggle ${label} failed`);
    } finally {
      setter(null);
    }
  };

  const handleToggleCertificates    = makeFeatureToggle(setTogglingCert,   adminAPI.toggleCertificates,        'certificates_enabled',        'Certificates');
  const handleToggleBiometric       = makeFeatureToggle(setTogglingBio,    adminAPI.toggleBiometric,           'biometric_enabled',           'Biometric');
  const handleToggleEmpBiometric    = makeFeatureToggle(setTogglingEmpBio, adminAPI.toggleEmployeeBiometric,   'employee_biometric_enabled',  'Employee Biometric');
  const handleToggleStuBiometric    = makeFeatureToggle(setTogglingStuBio, adminAPI.toggleStudentBiometric,    'student_biometric_enabled',   'Student Biometric');
  const handleToggleSMS             = makeFeatureToggle(setTogglingSMS,    adminAPI.toggleSMS,                 'sms_enabled',                 'SMS');

  const handleLoadSmsLog = async () => {
    if (smsLog !== null) { setSmsLog(null); return; }
    setSmsLogLoading(true);
    try {
      const res = await adminAPI.getSmsLog();
      setSmsLog(res.data.data || []);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load SMS log');
    } finally {
      setSmsLogLoading(false);
    }
  };

  const handleEditSave = async (code) => {
    setEditSaving(true);
    try {
      const res = await adminAPI.editSchool(code, editForm);
      setSaveSuccess(res.data.message);
      setSchools((prev) => prev.map((s) =>
        s.school_code === code ? { ...s, ...editForm } : s
      ));
      setEditingCode(null);
    } catch (err) {
      alert(err.response?.data?.message || 'Save failed');
    } finally {
      setEditSaving(false);
    }
  };

  const handleLoadStats = async (code) => {
    if (statsSchool === code) { setStatsSchool(null); return; }
    setStatsSchool(code);
    if (statsData[code]) return; // already loaded
    setStatsLoading(code);
    try {
      const res = await adminAPI.getStats(code);
      setStatsData((prev) => ({ ...prev, [code]: res.data.data }));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      setStatsData((prev) => ({ ...prev, [code]: { _error: msg } }));
    } finally {
      setStatsLoading(null);
    }
  };

  const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

  const filteredSchools = schools.filter((s) => {
    const q = searchQuery.toLowerCase();
    return !q || s.school_name.toLowerCase().includes(q) || s.school_code.toLowerCase().includes(q) || (s.contact_name || '').toLowerCase().includes(q);
  });

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.logo}>🏫</span>
          <div>
            <div style={styles.headerTitle}>Platform Admin</div>
            <div style={styles.headerSub}>School Management</div>
          </div>
        </div>
        <button style={styles.logoutBtn} onClick={onLogout}>🔒 Logout</button>
      </div>

      {/* Main */}
      <div style={styles.content}>
        {/* Stats strip */}
        {!loading && (
          <div style={styles.statsStrip}>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIcon, background: '#dbeafe', color: '#1d4ed8' }}>🏫</div>
              <div>
                <div style={styles.statValue}>{schools.length}</div>
                <div style={styles.statLabel}>Total Schools</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIcon, background: '#dcfce7', color: '#15803d' }}>✓</div>
              <div>
                <div style={styles.statValue}>{schools.filter(s => s.is_active).length}</div>
                <div style={styles.statLabel}>Active</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIcon, background: '#fee2e2', color: '#b91c1c' }}>⏸</div>
              <div>
                <div style={styles.statValue}>{schools.filter(s => !s.is_active).length}</div>
                <div style={styles.statLabel}>Inactive</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={{ ...styles.statIcon, background: '#dcfce7', color: '#15803d' }}>💬</div>
              <div>
                <div style={styles.statValue}>{schools.filter(s => s.whatsapp_enabled).length}</div>
                <div style={styles.statLabel}>WhatsApp On</div>
              </div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div style={styles.toolbar}>
          <h2 style={styles.sectionTitle}>All Schools <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: 15 }}>({filteredSchools.length}{searchQuery ? ` / ${schools.length}` : ''})</span></h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              style={styles.searchInput}
              placeholder="🔍 Search by name or code…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              style={{ ...styles.addBtn, background: smsLog !== null ? '#0f172a' : '#64748b' }}
              onClick={handleLoadSmsLog}
            >{smsLogLoading ? '…' : (smsLog !== null ? '📋 Hide SMS Log' : '📋 SMS Log')}</button>
            <button style={styles.addBtn} onClick={() => { setShowForm(true); setSaveError(''); setSaveSuccess(''); }}>
              + Add School
            </button>
          </div>
        </div>

        {saveSuccess && <div style={styles.successBanner}>{saveSuccess}</div>}
        {error       && <div style={styles.errorBanner}>{error}</div>}

        {/* Add School Form */}
        {showForm && (
          <div style={styles.formCard}>
            <h3 style={{ margin: '0 0 16px' }}>New School</h3>
            <form onSubmit={handleSubmit} autoComplete="off">
              <div style={styles.formGrid}>
                <label style={styles.label}>
                  School Name *
                  <input
                    style={styles.input}
                    name="school_name"
                    value={form.school_name}
                    onChange={handleChange}
                    required
                    placeholder="Geetham High School"
                  />
                </label>
                <label style={styles.label}>
                  School Code * <small>(2-30: a-z 0-9 _)</small>
                  <input
                    style={styles.input}
                    name="school_code"
                    value={form.school_code}
                    onChange={handleChange}
                    required
                    placeholder="geetham_hyd"
                    pattern="[a-z0-9_]{2,30}"
                  />
                </label>
                <label style={styles.label}>
                  School Location
                  <input
                    style={styles.input}
                    name="school_location"
                    value={form.school_location}
                    onChange={handleChange}
                    placeholder="e.g. Damaragidda"
                  />
                </label>
                <label style={styles.label}>
                  School Tagline
                  <input
                    style={styles.input}
                    name="school_tagline"
                    value={form.school_tagline}
                    onChange={handleChange}
                    placeholder="e.g. Nurturing Bright Futures"
                  />
                </label>
                <label style={styles.label}>
                  Admin Username *
                  <input
                    style={styles.input}
                    name="admin_username"
                    value={form.admin_username}
                    onChange={handleChange}
                    required
                    placeholder="principal"
                    autoComplete="new-password"
                  />
                </label>
                <label style={styles.label}>
                  Admin Password *
                  <input
                    style={styles.input}
                    type="password"
                    name="admin_password"
                    value={form.admin_password}
                    onChange={handleChange}
                    required
                    placeholder="Default: Admin"
                    minLength={4}
                    autoComplete="new-password"
                  />
                  <small style={{ color: '#6b7280' }}>Default password is <strong>Admin</strong>. School can change it after first login.</small>
                </label>
                <label style={styles.label}>
                  Contact Name
                  <input
                    style={styles.input}
                    name="contact_name"
                    value={form.contact_name}
                    onChange={handleChange}
                    placeholder="Mr. Ramesh"
                  />
                </label>
                <label style={styles.label}>
                  Contact Mobile
                  <input
                    style={styles.input}
                    name="contact_mobile"
                    value={form.contact_mobile}
                    onChange={handleChange}
                    placeholder="9876543210"
                  />
                </label>
                <label style={styles.label}>
                  Contact Email
                  <input
                    style={styles.input}
                    name="contact_email"
                    value={form.contact_email}
                    onChange={handleChange}
                    placeholder="school@example.com"
                    type="email"
                  />
                </label>
                <label style={styles.label}>
                  Plan
                  <select style={styles.input} name="subscription_plan" value={form.subscription_plan} onChange={handleChange}>
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                  </select>
                </label>
              </div>
              {saveError && <div style={styles.errorBanner}>{saveError}</div>}
              <div style={styles.formActions}>
                <button type="submit" style={styles.saveBtn} disabled={saving}>
                  {saving ? 'Creating…' : 'Create School'}
                </button>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setSaveError(''); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Schools Table */}
        {loading ? (
          <div style={styles.center}>Loading…</div>
        ) : schools.length === 0 ? (
          <div style={styles.center}>No schools yet. Click <strong>+ Add School</strong> to create the first one.</div>
        ) : filteredSchools.length === 0 ? (
          <div style={styles.center}>No schools match "<strong>{searchQuery}</strong>"</div>
        ) : (
          <div className="table-container" style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['School', 'School Code', 'Admin/Username', 'Contact', 'Plan', 'Features', 'Created', 'Actions', ''].map((h, i) => (
                    <th key={i} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredSchools.map((s) => (
                  <React.Fragment key={s.school_id}>
                  <tr style={styles.tr}>
                    {/* School: name + location */}
                    <td style={styles.schoolNameCell}>
                      <div style={styles.schoolName}>{s.school_name}</div>
                      {s.school_location && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>📍 {s.school_location}</div>
                      )}
                    </td>

                    {/* School Code */}
                    <td style={styles.td}>
                      <div style={styles.schoolCode}>{s.school_code}</div>
                    </td>

                    {/* Admin user + tagline */}
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600, color: '#1e293b' }}>{s.admin_username}</div>
                      {s.school_tagline && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, fontStyle: 'italic' }}>"{s.school_tagline}"</div>
                      )}
                    </td>

                    {/* Contact: name + mobile + email */}
                    <td style={styles.contactCell}>
                      <div style={styles.contactName}>{s.contact_name || '—'}</div>
                      {s.contact_mobile && <div style={styles.contactMobile}>📞 {s.contact_mobile}</div>}
                      {s.contact_email && <div style={{ ...styles.contactMobile, color: '#64748b' }}>✉ {s.contact_email}</div>}
                    </td>

                    {/* Plan badge */}
                    <td style={styles.td}>
                      <span style={{
                        ...styles.planBadge,
                        background: s.subscription_plan === 'pro' ? '#fef3c7' : '#e0e7ff',
                        color:      s.subscription_plan === 'pro' ? '#92400e' : '#3730a3',
                      }}>{s.subscription_plan || 'basic'}</span>
                    </td>

                    {/* Features: 4 pill toggles in one cell */}
                    <td style={styles.pillsCell}>
                      <div style={styles.pillRow}>
                        <button
                          style={{ ...styles.pill, ...(s.is_active ? styles.pillOn : styles.pillOff), ...(toggling === s.school_code ? styles.pillBusy : null) }}
                          disabled={toggling === s.school_code}
                          onClick={() => handleToggleActive(s)}
                          title={s.is_active ? 'Deactivate school' : 'Activate school'}
                        >{toggling === s.school_code ? '…' : (s.is_active ? '● Active' : '○ Inactive')}</button>
                        <button
                          style={{ ...styles.pill, ...(s.whatsapp_enabled ? styles.pillOn : styles.pillOff), ...(togglingWA === s.school_code ? styles.pillBusy : null) }}
                          disabled={togglingWA === s.school_code}
                          onClick={() => handleToggleWhatsApp(s)}
                          title={s.whatsapp_enabled ? 'Disable WhatsApp' : 'Enable WhatsApp'}
                        >{togglingWA === s.school_code ? '…' : '💬 WhatsApp'}</button>
                        <button
                          style={{ ...styles.pill, ...(s.certificates_enabled ? styles.pillOn : styles.pillOff), ...(togglingCert === s.school_code ? styles.pillBusy : null) }}
                          disabled={togglingCert === s.school_code}
                          onClick={() => handleToggleCertificates(s)}
                          title={s.certificates_enabled ? 'Disable Certificates' : 'Enable Certificates'}
                        >{togglingCert === s.school_code ? '…' : '📜 Certs'}</button>
                        <button
                          style={{ ...styles.pill, ...(s.employee_biometric_enabled ? styles.pillOn : styles.pillOff), ...(togglingEmpBio === s.school_code ? styles.pillBusy : null) }}
                          disabled={togglingEmpBio === s.school_code}
                          onClick={() => handleToggleEmpBiometric(s)}
                          title={s.employee_biometric_enabled ? 'Disable Employee Biometric' : 'Enable Employee Biometric'}
                        >{togglingEmpBio === s.school_code ? '…' : '👆 Emp Bio'}</button>
                        <button
                          style={{ ...styles.pill, ...(s.student_biometric_enabled ? styles.pillOn : styles.pillOff), ...(togglingStuBio === s.school_code ? styles.pillBusy : null) }}
                          disabled={togglingStuBio === s.school_code}
                          onClick={() => handleToggleStuBiometric(s)}
                          title={s.student_biometric_enabled ? 'Disable Student Biometric' : 'Enable Student Biometric'}
                        >{togglingStuBio === s.school_code ? '…' : '🎓 Stu Bio'}</button>
                        <button
                          style={{ ...styles.pill, ...(s.sms_enabled ? styles.pillOn : styles.pillOff), ...(togglingSMS === s.school_code ? styles.pillBusy : null) }}
                          disabled={togglingSMS === s.school_code}
                          onClick={() => handleToggleSMS(s)}
                          title={s.sms_enabled ? 'Disable SMS' : 'Enable SMS'}
                        >{togglingSMS === s.school_code ? '…' : '📱 SMS'}</button>
                      </div>
                    </td>

                    {/* Created */}
                    <td style={{ ...styles.td, color: '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                      {new Date(s.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>

                    {/* Actions */}
                    <td style={styles.actionsCell}>
                      {resetting === s.school_code ? (
                        <div style={styles.confirmBox}>
                          <div style={{fontSize:'.82rem',color:'#1e40af',marginBottom:6,fontWeight:600}}>🔑 Set temporary password for {s.school_name}</div>
                          <div style={styles.confirmRow}>
                            <input
                              style={styles.confirmInput}
                              type="password"
                              placeholder="New temp password (min 4 chars)"
                              value={resetPass}
                              onChange={(e) => setResetPass(e.target.value)}
                            />
                            <button
                              style={{ ...styles.deleteConfirmBtn, background: '#1d4ed8', opacity: resetPass.length >= 4 ? 1 : 0.4 }}
                              onClick={() => handleResetPassword(s)}
                            >Set</button>
                            <button style={styles.cancelSmallBtn} onClick={() => { setResetting(null); setResetPass(''); }}>Cancel</button>
                          </div>
                          <div style={{fontSize:'.78rem',color:'#6b7280',marginTop:4}}>School will be forced to change password on next login.</div>
                        </div>
                      ) : (
                        <div style={{display:'flex',gap:4,flexWrap:'nowrap'}}>
                          <button
                            style={styles.iconBtn}
                            title="Edit school details"
                            onClick={() => {
                              setEditingCode(s.school_code);
                              setEditForm({
                                school_name: s.school_name,
                                school_location: s.school_location || '',
                                contact_name: s.contact_name || '',
                                contact_mobile: s.contact_mobile || '',
                                contact_email: s.contact_email || '',
                                subscription_plan: s.subscription_plan || 'basic',
                                razorpay_linked_account_id: s.razorpay_linked_account_id || '',
                                razorpay_account_status: s.razorpay_account_status || 'not_created',
                                platform_fee_pct: s.platform_fee_pct ?? 1.0,
                                msg91_authkey: s.msg91_authkey || '',
                                msg91_template_id: s.msg91_template_id || '',
                                msg91_sender_id: s.msg91_sender_id || '',
                              });
                              setResetting(null); setDeleting(null); setSaveSuccess('');
                            }}
                          >✏️</button>
                          <button
                            style={styles.iconBtn}
                            title="Reset school-admin password"
                            onClick={() => { setResetting(s.school_code); setResetPass(''); setDeleting(null); setSaveSuccess(''); }}
                          >🔑</button>
                          <button
                            style={styles.iconBtn}
                            title="Manage staff / sub-user passwords"
                            onClick={() => loadSubUsers(s)}
                          >👥</button>
                          <button
                            style={styles.iconBtnDanger}
                            title="Delete school"
                            onClick={() => { setDeleting(s.school_code); setConfirmCode(''); setResetting(null); setSaveSuccess(''); }}
                          >🗑️</button>
                        </div>
                      )}
                      {deleting === s.school_code && (
                        <div style={styles.confirmBox}>
                          <div style={styles.confirmWarning}>⚠️ This will permanently delete ALL student &amp; payment data!</div>
                          <div style={styles.confirmRow}>
                            <input
                              style={styles.confirmInput}
                              placeholder={`Type "${s.school_code}" to confirm`}
                              value={confirmCode}
                              onChange={(e) => setConfirmCode(e.target.value)}
                            />
                            <button
                              style={{ ...styles.deleteConfirmBtn, opacity: confirmCode === s.school_code ? 1 : 0.4 }}
                              onClick={() => handleDelete(s)}
                            >Delete</button>
                            <button style={styles.cancelSmallBtn} onClick={() => { setDeleting(null); setConfirmCode(''); }}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Stats cell */}
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button
                        style={statsSchool === s.school_code ? styles.statsActiveBtn : styles.statsBtn}
                        onClick={() => handleLoadStats(s.school_code)}
                        title="View school statistics"
                      >📊</button>
                    </td>
                  </tr>

                  {/* ── Staff / sub-user password reset panel ── */}
                  {usersPanelSchool === s.school_code && (
                    <tr style={{ background: '#eef4ff' }}>
                      <td colSpan="9" style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, marginBottom: 10, color: '#1e3a8a' }}>
                          👥 Staff accounts — {s.school_name}
                        </div>
                        {subUsersLoading && !subUsers[s.school_code] ? (
                          <div style={{ color: '#64748b' }}>Loading…</div>
                        ) : (subUsers[s.school_code] || []).length === 0 ? (
                          <div style={{ color: '#64748b', fontStyle: 'italic' }}>
                            No staff accounts. The school admin can create them from Operations → Users.
                          </div>
                        ) : (
                          <div className="table-container">
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9rem' }}>
                            <thead>
                              <tr style={{ background: '#dbeafe' }}>
                                <th style={{ ...styles.th, padding: '6px 10px' }}>Username</th>
                                <th style={{ ...styles.th, padding: '6px 10px' }}>Full Name</th>
                                <th style={{ ...styles.th, padding: '6px 10px' }}>Tabs</th>
                                <th style={{ ...styles.th, padding: '6px 10px' }}>Status</th>
                                <th style={{ ...styles.th, padding: '6px 10px', textAlign: 'right' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(subUsers[s.school_code] || []).map((u) => (
                                <tr key={u.user_id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                  <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{u.username}</td>
                                  <td style={{ padding: '6px 10px' }}>{u.full_name || '—'}</td>
                                  <td style={{ padding: '6px 10px', color: '#475569', fontSize: '.82rem' }}>{u.role || '—'}</td>
                                  <td style={{ padding: '6px 10px' }}>
                                    {u.is_active
                                      ? <span style={{ color: '#166534' }}>Active</span>
                                      : <span style={{ color: '#991b1b' }}>Disabled</span>}
                                    {u.must_change_password ? (
                                      <span style={{ marginLeft: 8, background: '#fff3cd', color: '#856404', border: '1px solid #ffe08a', padding: '2px 6px', borderRadius: 6, fontSize: '.72rem' }}>
                                        must change
                                      </span>
                                    ) : null}
                                  </td>
                                  <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                                    {resettingUser === u.user_id ? (
                                      <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                        <input
                                          style={{ ...styles.confirmInput, minWidth: 180 }}
                                          type="password"
                                          placeholder="Temp password (min 4)"
                                          value={userResetPass}
                                          onChange={(e) => setUserResetPass(e.target.value)}
                                          autoFocus
                                        />
                                        <button
                                          style={{ ...styles.deleteConfirmBtn, background: '#1d4ed8', opacity: userResetPass.length >= 4 ? 1 : 0.4 }}
                                          onClick={() => handleResetSubUser(s.school_code, u)}
                                        >Set</button>
                                        <button
                                          style={styles.cancelSmallBtn}
                                          onClick={() => { setResettingUser(null); setUserResetPass(''); }}
                                        >Cancel</button>
                                      </div>
                                    ) : (
                                      <button
                                        style={styles.iconBtn}
                                        title="Reset this user's password"
                                        onClick={() => { setResettingUser(u.user_id); setUserResetPass(''); }}
                                      >🔑 Reset</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        )}
                        <div style={{ fontSize: '.78rem', color: '#6b7280', marginTop: 8 }}>
                          Reset here forces the staff member to set a new password on their next login. The <em>school-admin</em> password is reset with the 🔑 button in the Actions column.
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* ── Edit inline form ── */}
                  {editingCode === s.school_code && (
                    <tr style={{ background: '#fffde7' }}>
                      <td colSpan="9" style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, marginBottom: 10, color: '#1a237e' }}>✏️ Edit — {s.school_name}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 10 }}>
                          {[
                            { label: 'School Name *', key: 'school_name', type: 'text' },
                            { label: 'School Location', key: 'school_location', type: 'text' },
                            { label: 'Contact Person', key: 'contact_name', type: 'text' },
                            { label: 'Mobile', key: 'contact_mobile', type: 'text' },
                            { label: 'Email', key: 'contact_email', type: 'email' },
                          ].map(({ label, key, type }) => (
                            <label key={key} style={styles.label}>
                              {label}
                              <input
                                style={styles.input}
                                type={type}
                                value={editForm[key] || ''}
                                onChange={(e) => setEditForm((p) => ({ ...p, [key]: e.target.value }))}
                              />
                            </label>
                          ))}
                          <label style={styles.label}>
                            Plan
                            <select style={styles.input} value={editForm.subscription_plan || 'basic'} onChange={(e) => setEditForm((p) => ({ ...p, subscription_plan: e.target.value }))}>
                              <option value="basic">Basic</option>
                              <option value="pro">Pro</option>
                            </select>
                          </label>
                        </div>

                        {/* ── Online Payments (Razorpay Route) ── */}
                        <div style={{ marginTop: 14, padding: 10, background: '#f1f8e9', borderRadius: 6, border: '1px solid #c5e1a5' }}>
                          <div style={{ fontWeight: 700, marginBottom: 8, color: '#33691e' }}> 💳 Online Payments (Razorpay Route)</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 10 }}>
                            <label style={styles.label}>
                              Linked Account ID
                              <input
                                style={styles.input}
                                type="text"
                                placeholder="acc_XXXXXXXX"
                                value={editForm.razorpay_linked_account_id || ''}
                                onChange={(e) => setEditForm((p) => ({ ...p, razorpay_linked_account_id: e.target.value.trim() }))}
                              />
                            </label>
                            <label style={styles.label}>
                              KYC Status
                              <select
                                style={styles.input}
                                value={editForm.razorpay_account_status || 'not_created'}
                                onChange={(e) => setEditForm((p) => ({ ...p, razorpay_account_status: e.target.value }))}
                              >
                                <option value="not_created">Not created</option>
                                <option value="under_review">Under review</option>
                                <option value="activated">Activated</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            </label>
                            <label style={styles.label}>
                              Platform Fee %
                              <input
                                style={styles.input}
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={editForm.platform_fee_pct ?? 1.0}
                                onChange={(e) => setEditForm((p) => ({ ...p, platform_fee_pct: e.target.value }))}
                              />
                            </label>
                          </div>
                          <div style={{ fontSize: 11, color: '#558b2f', marginTop: 6 }}>
                            Create a Linked Account on Razorpay dashboard → Route → Accounts, then paste the <code>acc_XXXX</code> id and set status to <strong>Activated</strong>.
                          </div>
                        </div>
                        {/* ── /Online Payments ── */}

                        {/* ── SMS (MSG91 — per-school credentials) ── */}
                        <div style={{ marginTop: 14, padding: 10, background: '#e8f4fd', borderRadius: 6, border: '1px solid #90caf9' }}>
                          <div style={{ fontWeight: 700, marginBottom: 8, color: '#0d47a1' }}> 📱 SMS (MSG91 — each school its own DLT account)</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px,1fr))', gap: 10 }}>
                            <label style={styles.label}>
                              MSG91 Authkey
                              <input
                                style={styles.input}
                                type="text"
                                placeholder="Leave blank to use platform default"
                                value={editForm.msg91_authkey || ''}
                                onChange={(e) => setEditForm((p) => ({ ...p, msg91_authkey: e.target.value.trim() }))}
                                autoComplete="off"
                              />
                            </label>
                            <label style={styles.label}>
                              DLT Template ID (payment)
                              <input
                                style={styles.input}
                                type="text"
                                placeholder="e.g. 64a1b2c3d4e5f6789012345"
                                value={editForm.msg91_template_id || ''}
                                onChange={(e) => setEditForm((p) => ({ ...p, msg91_template_id: e.target.value.trim() }))}
                                autoComplete="off"
                              />
                            </label>
                            <label style={styles.label}>
                              DLT Sender ID (header)
                              <input
                                style={styles.input}
                                type="text"
                                placeholder="e.g. GTHSCH (3-11 A-Z 0-9)"
                                maxLength={11}
                                value={editForm.msg91_sender_id || ''}
                                onChange={(e) => setEditForm((p) => ({ ...p, msg91_sender_id: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                                autoComplete="off"
                              />
                            </label>
                          </div>
                          <div style={{ fontSize: 11, color: '#0d47a1', marginTop: 6 }}>
                            Each school has its <strong>own MSG91 account</strong>, DLT-registered template, and sender header.
                            Leave any field blank to fall back to the platform's default (env vars on the server).
                            Then toggle <strong>📱 SMS</strong> in the schools table to enable sending. Template variables must be in order: <code>##VAR1##</code>=name, <code>##VAR2##</code>=amount, <code>##VAR3##</code>=receipt, <code>##VAR4##</code>=date.
                          </div>
                        </div>
                        {/* ── /SMS ── */}

                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button style={styles.saveBtn} disabled={editSaving} onClick={() => handleEditSave(s.school_code)}>
                            {editSaving ? 'Saving…' : '💾 Save'}
                          </button>
                          <button style={styles.cancelBtn} onClick={() => setEditingCode(null)}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* ── Stats expandable row ── */}
                  {statsSchool === s.school_code && (
                    <tr style={{ background: '#e3f2fd' }}>
                      <td colSpan="9" style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, marginBottom: 8, color: '#1a237e' }}>📊 Stats — {s.school_name}</div>
                        {statsLoading === s.school_code || statsData[s.school_code] === undefined ? (
                          <span style={{ color: '#607d8b' }}>Loading…</span>
                        ) : statsData[s.school_code] && !statsData[s.school_code]._error ? (
                          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                            {[
                              { label: 'Total Students',    val: statsData[s.school_code].total_students   },
                              { label: 'Active',            val: statsData[s.school_code].active_students  },
                              { label: 'Archived',          val: statsData[s.school_code].archived_students},
                              { label: 'Total Payments',    val: statsData[s.school_code].total_payments   },
                              { label: 'Total Collected',   val: fmt(statsData[s.school_code].total_collected), color: '#2e7d32' },
                              { label: 'Pending Dues',      val: fmt(statsData[s.school_code].total_pending),   color: '#c62828' },
                            ].map(({ label, val, color }) => (
                              <div key={label} style={{ textAlign: 'center', minWidth: 100 }}>
                                <div style={{ fontSize: 22, fontWeight: 700, color: color || '#1a237e' }}>{val}</div>
                                <div style={{ fontSize: 11, color: '#607d8b', marginTop: 2 }}>{label}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#c62828' }}>Error: {statsData[s.school_code]?._error || 'Failed to load stats'}</span>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* SMS Log Panel */}
        {smsLog !== null && (
          <div style={{ marginTop: 24, background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(15,23,42,0.08)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>📋 SMS Audit Log (last 200)</span>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#64748b' }} onClick={() => setSmsLog(null)}>✕</button>
            </div>
            {smsLog.length === 0 ? (
              <div style={{ padding: 24, color: '#64748b', textAlign: 'center' }}>No SMS records yet.</div>
            ) : (
              <div className="table-container">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['School', 'Student ID', 'Mobile', 'Status', 'Skipped / Error', 'Sent At'].map((h) => (
                      <th key={h} style={{ background: '#f8fafc', color: '#475569', padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {smsLog.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '9px 14px', fontWeight: 600 }}>{row.school_code}</td>
                      <td style={{ padding: '9px 14px', color: '#64748b' }}>{row.student_id}</td>
                      <td style={{ padding: '9px 14px', fontFamily: 'monospace' }}>{row.mobile || '—'}</td>
                      <td style={{ padding: '9px 14px' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                          background: row.status === 'sent' ? '#dcfce7' : row.status === 'skipped' ? '#fef9c3' : '#fee2e2',
                          color:      row.status === 'sent' ? '#15803d' : row.status === 'skipped' ? '#92400e' : '#b91c1c',
                        }}>{row.status}</span>
                      </td>
                      <td style={{ padding: '9px 14px', color: '#94a3b8', fontSize: 12 }}>{row.skipped || row.error || '—'}</td>
                      <td style={{ padding: '9px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{row.sent_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page:          { minHeight: '100vh', background: '#f1f5f9', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', color: '#0f172a' },
  header:        { background: 'linear-gradient(135deg, #1e3a8a 0%, #312e81 100%)', color: '#fff', padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(30,58,138,0.18)' },
  headerLeft:    { display: 'flex', alignItems: 'center', gap: 14 },
  logo:          { fontSize: 32 },
  headerTitle:   { fontWeight: 700, fontSize: 18, letterSpacing: 0.2 },
  headerSub:     { fontSize: 12, opacity: 0.75, marginTop: 2 },
  logoutBtn:     { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  content:       { maxWidth: '100%', margin: '0 auto', padding: '24px 32px', width: '100%', boxSizing: 'border-box' },

  // ── Stats strip (new) ──────────────────────────────────────────────────
  statsStrip:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 },
  statCard:      { background: '#fff', padding: '16px 20px', borderRadius: 10, boxShadow: '0 1px 3px rgba(15,23,42,0.06)', display: 'flex', alignItems: 'center', gap: 14 },
  statIcon:      { fontSize: 26, width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  statValue:     { fontSize: 24, fontWeight: 700, color: '#0f172a', lineHeight: 1 },
  statLabel:     { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 5 },

  // ── Toolbar ────────────────────────────────────────────────────────────
  toolbar:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' },
  sectionTitle:  { margin: 0, fontSize: 20, color: '#1e293b', fontWeight: 700 },
  searchInput:   { border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 14px', fontSize: 14, outline: 'none', width: 280, background: '#fff' },
  addBtn:        { background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, boxShadow: '0 1px 3px rgba(29,78,216,0.3)' },
  successBanner: { background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '10px 16px', marginBottom: 12, color: '#15803d', fontWeight: 500 },
  errorBanner:   { background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 16px', marginBottom: 12, color: '#b91c1c', fontWeight: 500 },
  formCard:      { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(15,23,42,0.08)', marginBottom: 24 },
  formGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  label:         { display: 'flex', flexDirection: 'column', fontSize: 13, fontWeight: 600, color: '#334155', gap: 4 },
  input:         { border: '1px solid #cbd5e1', borderRadius: 6, padding: '8px 10px', fontSize: 14, outline: 'none', fontWeight: 400 },
  formActions:   { display: 'flex', gap: 12, marginTop: 20 },
  saveBtn:       { background: '#15803d', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 22px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  cancelBtn:     { background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 22px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  center:        { textAlign: 'center', padding: 40, color: '#64748b', background: '#fff', borderRadius: 10, boxShadow: '0 1px 3px rgba(15,23,42,0.06)' },

  // ── Table ──────────────────────────────────────────────────────────────
  tableWrap:     { background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(15,23,42,0.08)', overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' },
  table:         { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th:            { background: '#f8fafc', color: '#475569', padding: '14px 18px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #e2e8f0' },
  tr:            { borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' },
  td:            { padding: '14px 18px', color: '#1e293b', verticalAlign: 'middle' },
  schoolNameCell:{ padding: '14px 18px', verticalAlign: 'middle' },
  schoolName:    { fontWeight: 700, color: '#0f172a', fontSize: 14 },
  schoolCode:    { color: '#1d4ed8', fontFamily: 'ui-monospace, "SF Mono", monospace', fontSize: 12, marginTop: 3, fontWeight: 500 },
  contactCell:   { padding: '14px 18px', verticalAlign: 'middle' },
  contactName:   { fontSize: 14, color: '#1e293b', fontWeight: 500 },
  contactMobile: { fontSize: 12, color: '#64748b', marginTop: 2 },
  planBadge:     { display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, background: '#e0e7ff', color: '#3730a3' },

  // ── Feature pills (modernized toggles) ─────────────────────────────────
  pillsCell:     { padding: '14px 18px', verticalAlign: 'middle' },
  pillRow:       { display: 'flex', gap: 6, flexWrap: 'wrap' },
  pill:          { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: 0.3, cursor: 'pointer', border: 'none', transition: 'transform 0.1s' },
  pillOn:        { background: '#dcfce7', color: '#15803d', boxShadow: 'inset 0 0 0 1px #86efac' },
  pillOff:       { background: '#f1f5f9', color: '#94a3b8', boxShadow: 'inset 0 0 0 1px #e2e8f0' },
  pillBusy:      { opacity: 0.6, cursor: 'wait' },

  // ── Action buttons ─────────────────────────────────────────────────────
  actionsCell:   { padding: '14px 18px', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  iconBtn:       { background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 14, marginRight: 4, transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  iconBtnDanger: { background: '#fff', border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 14, marginRight: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },

  // ── Confirm boxes (kept compatible with existing logic) ────────────────
  deleteBtn:         { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
  resetBtn:          { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
  editBtn:           { background: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
  activateBtn:       { background: '#dcfce7', color: '#15803d', border: '1px solid #86efac', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
  deactivateBtn:     { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
  confirmBox:        { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 280, marginTop: 8 },
  confirmWarning:    { fontSize: 12, color: '#b91c1c', fontWeight: 600 },
  confirmRow:        { display: 'flex', gap: 6, alignItems: 'center' },
  confirmInput:      { border: '1px solid #fca5a5', borderRadius: 6, padding: '6px 10px', fontSize: 13, flex: 1, outline: 'none' },
  deleteConfirmBtn:  { background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  cancelSmallBtn:    { background: '#fff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 },

  // ── Legacy aliases (kept so older inline usages keep working) ──────────
  tdCode:        { padding: '14px 18px', color: '#1d4ed8', fontWeight: 600, fontFamily: 'monospace', fontSize: 14 },
  statsBtn:      { background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 15 },
  statsActiveBtn:{ background: '#1d4ed8', color: '#fff',    border: '1px solid #1d4ed8', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 15 },
  waOnBtn:       { background: '#dcfce7', color: '#15803d', border: '1px solid #86efac', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  waOffBtn:      { background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  featOnBtn:     { background: '#dcfce7', color: '#15803d', border: '1px solid #86efac', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  featOffBtn:    { background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
};
